/**
 * @fileoverview The student's Tasks Tracker grid — the same matrix the teacher sees (one top-level column
 * per lesson, own scroll container with sticky header and sticky first column, legend footer), with a
 * single row: the logged-in student.
 */

import { memo, useMemo } from 'react'
import { Box, Paper, Table } from '@mantine/core'
import type { StudentTrackerMatrix as Matrix } from '../types'
import {
  FINAL_SUBCOLS,
  STUDENT_COLUMN_MIN_WIDTH,
  SUB_COLUMN_MIN_WIDTH,
  legendFooterStyle,
  scrollContainerStyle,
  subColCount,
  tableStyle,
} from './matrixStyles'
import { StudentTrackerLegend } from './StudentTrackerLegend'
import { StudentTrackerMatrixHead } from './StudentTrackerMatrixHead'
import { StudentTrackerMatrixRow, type MatrixRowHandlers } from './StudentTrackerMatrixRow'

interface StudentTrackerMatrixProps extends MatrixRowHandlers {
  matrix: Matrix
  editable: boolean
  today: string
}

export const StudentTrackerMatrix = memo(function StudentTrackerMatrix({
  matrix,
  editable,
  today,
  ...handlers
}: StudentTrackerMatrixProps) {
  const totalLessonSubcols = useMemo(
    () => matrix.lessons.reduce((sum, lesson) => sum + subColCount(lesson), 0),
    [matrix.lessons],
  )
  const tableMinWidth = STUDENT_COLUMN_MIN_WIDTH + (totalLessonSubcols + FINAL_SUBCOLS) * SUB_COLUMN_MIN_WIDTH

  return (
    <Paper withBorder radius='md' sx={{ overflow: 'hidden' }}>
      <Box style={scrollContainerStyle}>
        <Table
          withColumnBorders
          horizontalSpacing='sm'
          verticalSpacing='md'
          style={{ ...tableStyle, minWidth: tableMinWidth }}
        >
          <StudentTrackerMatrixHead lessons={matrix.lessons} />
          <tbody>
            <StudentTrackerMatrixRow
              matrix={matrix}
              editable={editable}
              today={today}
              totalColumns={totalLessonSubcols + FINAL_SUBCOLS}
              {...handlers}
            />
          </tbody>
        </Table>
      </Box>
      <Box style={legendFooterStyle}>
        <StudentTrackerLegend />
      </Box>
    </Paper>
  )
})
